import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { commentsQueryKeyPrefix } from './keys';
import type { CommentItem, CommentSource, CommentsPage } from './models';

export function addCommentToCache(
  queryClient: QueryClient,
  source: CommentSource,
  comment: CommentItem,
  parentCommentId?: string,
) {
  const commentToInsert =
    parentCommentId && !comment.parent ? { ...comment, parent: parentCommentId } : comment;

  queryClient.setQueriesData<InfiniteData<CommentsPage>>(
    { queryKey: commentsQueryKeyPrefix(source) },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page, idx) => {
          if (idx !== 0) return page;
          return {
            ...page,
            comments: [commentToInsert, ...page.comments],
          };
        }),
      };
    },
  );
}

export type CommentsCacheSnapshot = [readonly unknown[], InfiniteData<CommentsPage> | undefined][];

export function updateCommentInCache(
  queryClient: QueryClient,
  source: CommentSource,
  commentId: string,
  updater: (c: CommentItem) => CommentItem,
) {
  queryClient.setQueriesData<InfiniteData<CommentsPage>>(
    { queryKey: commentsQueryKeyPrefix(source) },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          comments: page.comments.map((c) => (c.id === commentId ? updater(c) : c)),
        })),
      };
    },
  );
}

export function snapshotCommentsCaches(
  queryClient: QueryClient,
  source: CommentSource,
): CommentsCacheSnapshot {
  const caches = queryClient.getQueriesData<InfiniteData<CommentsPage>>({
    queryKey: commentsQueryKeyPrefix(source),
  });
  return caches.map(([key, data]) => [key, data]);
}

export function restoreCommentsCaches(queryClient: QueryClient, snapshot: CommentsCacheSnapshot) {
  for (const [key, data] of snapshot) {
    queryClient.setQueryData(key, data);
  }
}
